import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {useSuspenseQuery} from '@github-ui/react-query'
import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'
import {ActivityResponseSchema, type ActivityResponse} from '@github-ui/dotcom-schema/types/pull_requests/v1/activity'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {useEffectiveSelection} from './use-effective-selection'

export function useSuspenseActivityPageData() {
  const {data} = useSuspenseQuery(useActivityQueryOptions())
  return data
}

function useActivityQueryOptions() {
  const {effectiveSelection, lastActivityTimestamp} = useEffectiveSelection()
  const queryKey = [PageData.activity, effectiveSelection?.timestamp, lastActivityTimestamp] as const

  const since = effectiveSelection?.timestamp ?? ''
  // Fold `since` into the path *before* `getInsightsUrl` runs (inside
  // `usePageDataUrl`) so insights query params (e.g. `_tracing`,
  // `disable_clusters`) and `since` merge into a single query string. Appending
  // `since` separately via `queryFnFetch` would emit a second `?` whenever an
  // insights query string is already present, silently dropping the required
  // `since` filter.
  const pathname = usePageDataUrl(PageData.activity, new URLSearchParams({since}).toString())

  return {
    queryKey,
    // the since query param is required, so we can't enable the query if it's not set
    enabled: !!effectiveSelection,
    queryFn: () => fetchActivityPageData(pathname),
    staleTime: Infinity,
    // Declarative proto persister: `useSuspenseQuery`'s schema-aware overload
    // expands this config into a `createProtoPersister` (validated by
    // `ActivityResponseSchema`), so the activity sidebar paints from IndexedDB
    // on revisit and refetches in the background.
    schema: ActivityResponseSchema,
    persister: {enabled: true},
  }
}

async function fetchActivityPageData(pathname: string): Promise<ActivityResponse> {
  // `queryFnFetch` decodes the proto3-JSON response into the canonical
  // `ActivityResponse`, which the sidebar rows consume directly.
  return queryFnFetch(
    {
      queryDeps: {pathname},
      routeId: 'pull-request-activity',
      queryName: 'activityPageData',
    },
    {schema: ActivityResponseSchema},
  )
}
